//React
import { useEffect, useState, FunctionComponent } from "react";

//Models
import { NumberFacet } from "../../models/facets/number-facet";

//Radix UI
import * as Toggle from "@radix-ui/react-toggle";

//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

//Selectors
import { selectSelectedFacets } from "../../redux/selectors/select-selected-facets";
import { selectVisibleNumberFacetValue } from "../../redux/selectors/select-visible-number-facet-value";

//Actions
import { setFacetValue } from "../../redux/slices/facet-slice";

//Util
import { isFacetSelected } from "../../functions/filtering/is-facet-selected";

interface FacetProps {
	drhFacet: NumberFacet;
}

const RadioButtonFacet: FunctionComponent<FacetProps> = (props) => {
	//Properties
	const { drhFacet } = props;

	//Store Dispatch
	const dispatch = useAppDispatch();

	//State Values
	const visibleFacetValue = useAppSelector(
		selectVisibleNumberFacetValue(drhFacet.fieldName),
	);

	const selectedFacets = useAppSelector(selectSelectedFacets);

	//Component State
	const [buttonValues, setButtonValues] = useState<number[]>([]);

	//Use Effects
	useEffect(() => {
		var facetValue = visibleFacetValue;
		if (facetValue === null) {
			return;
		}
		if (facetValue > 4) {
			facetValue = 4;
		}
		setButtonValues(getButtonValues(facetValue));
	}, [visibleFacetValue]);

	//Functions
	function getButtonValues(numberValue: number) {
		if (numberValue === undefined) {
			return;
		}

		//This will round numbers like 3.5 down to 3
		numberValue = Math.floor(numberValue);

		var valueRange = [];

		for (let index = 1; index < numberValue + 1; index++) {
			valueRange.push(index);
		}

		return valueRange;
	}

	const ariaLabel = drhFacet.title + " facet.";

	//JSX
	if (!buttonValues || buttonValues.length === 0) {
		return (
			<div className="facet">
				<h3>{drhFacet.title}</h3>
				<div>No facet values</div>
			</div>
		);
	}

	function getButtonClassName(facetValue: number) {
		var className = "CoveoFacetButtonListButton";

		if (isFacetSelected(selectedFacets, drhFacet, facetValue)) {
			className += " selected";
		}
		return className + " Toggle";
	}

	function getButtonId(facetTitle: string, facetValue: number) {
		return facetTitle.replace(/ +/g, "_") + "_" + facetValue.toString();
	}

	return (
		<span>
			<div className="coveo-facet-header">
				<div className="coveo-facet-header-title-section">
					<div
						className="coveo-facet-header-title"
						role="heading"
						aria-label={ariaLabel}
						aria-level={2}
					>
						{drhFacet.title}
					</div>
				</div>
				<div className="CoveoFacetButtonList">
					{buttonValues.map((value) => (
						<Toggle.Root
							className={getButtonClassName(value)}
							aria-label={ariaLabel}
							aria-level={2}
							title={value.toString()}
							onClick={() =>
								dispatch(
									setFacetValue({
										facet: drhFacet,
										facetValues: value,
									}),
								)
							}
							itemID={getButtonId(drhFacet.title, value)}
							key={getButtonId(drhFacet.title, value)}
						>
							{value.toString()}+
						</Toggle.Root>
					))}
				</div>
			</div>
		</span>
	);
};

export default RadioButtonFacet;
